<template>
    <div class="contents">
        <div class="container">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
                            <div class="d-flex align-items-center user-member__title justify-content-center mr-sm-25">
                                <h4 class="text-capitalize fw-500 breadcrumb-title">Koneksi Device</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-12">
                    <div class="scan-card global-shadow border bg-white radius-xl w-100 mb-30">
                        <div class="scan-header">
                            <div class="scan-header__icon">
                                <i class="fab fa-whatsapp"></i>
                            </div>
                            <div class="scan-header__info">
                                <h5 class="mb-1">{{ deviceName || 'Device' }}</h5>
                                <div class="scan-header__number-row">
                                    <span class="scan-header__number">{{ deviceNumber || 'Belum diisi' }}</span>
                                    <button type="button" class="btn-edit-number" v-on:click="openEditNumber" title="Edit nomor HP">
                                        <i class="fas fa-pencil-alt"></i>
                                    </button>
                                </div>
                            </div>
                            <div class="scan-header__status">
                                <span class="status-dot" :class="statusClass"></span>
                                {{ statusLabel }}
                            </div>
                        </div>

                        <div class="scan-body">
                            <div class="row">
                                <div class="col-lg-5">
                                    <div class="scan-instructions">
                                        <h6 class="scan-section-title">Hubungkan dengan QR Code</h6>

                                        <ol class="scan-steps">
                                            <li>Buka <strong>WhatsApp</strong> di HP Anda</li>
                                            <li>Tap <strong>Menu</strong> atau <strong>Settings</strong> → <strong>Linked Devices</strong></li>
                                            <li>Tap <strong>Link a device</strong></li>
                                            <li>Arahkan kamera HP ke QR Code di sebelah kanan</li>
                                        </ol>

                                        <div v-if="phonePairingEnabled" class="mode-toggle">
                                            <button
                                                type="button"
                                                class="mode-toggle__btn"
                                                :class="{ active: pairingMode === 'qr' }"
                                                v-on:click="switchPairingMode('qr')"
                                                :disabled="pairingLoading && pairingMode !== 'qr'"
                                            >
                                                <i class="fas fa-qrcode mr-1"></i> QR Code
                                                <span v-if="pairingLoading && pairingMode === 'qr'" class="spinner-border spinner-border-sm ml-1"></span>
                                            </button>
                                            <button
                                                type="button"
                                                class="mode-toggle__btn"
                                                :class="{ active: pairingMode === 'phone' }"
                                                v-on:click="switchPairingMode('phone')"
                                                :disabled="pairingLoading || phoneCooldownActive || (!qrReady && !pairingCodeReady)"
                                                :title="phoneCooldownActive ? `Tunggu ${phoneCooldownSeconds} detik` : (!qrReady && !pairingCodeReady ? 'Tunggu QR muncul dulu' : '')"
                                            >
                                                <i class="fas fa-mobile-alt mr-1"></i> Nomor Telepon
                                                <span v-if="pairingLoading && pairingMode === 'phone'" class="spinner-border spinner-border-sm ml-1"></span>
                                            </button>
                                        </div>
                                        <template v-if="phonePairingEnabled">
                                            <p v-if="phoneCooldownActive" class="scan-hint mode-hint text-warning">
                                                <i class="fas fa-clock mr-1"></i>
                                                Rate limit WhatsApp. Coba lagi dalam {{ phoneCooldownSeconds }} detik.
                                            </p>
                                            <p v-else-if="!qrReady && !pairingCodeReady && !isLoading" class="scan-hint mode-hint">
                                                <i class="fas fa-info-circle mr-1"></i>
                                                Pairing via nomor tersedia setelah QR muncul.
                                            </p>
                                        </template>

                                        <div class="scan-actions">
                                            <button
                                                v-if="repairLoading"
                                                class="btn btn-outline-primary btn-block"
                                                disabled
                                            >
                                                <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                Memindahkan server...
                                            </button>
                                            <button
                                                v-else
                                                type="button"
                                                v-on:click="repairDevice"
                                                class="btn btn-outline-primary btn-block"
                                            >
                                                <i class="fas fa-server mr-1"></i>
                                                Pindah Server
                                                <span class="badge badge-danger ml-1">New</span>
                                            </button>
                                            <p class="scan-hint">
                                                <i class="fas fa-info-circle mr-1"></i>
                                                Setelah klik pindah server, tunggu QR muncul atau refresh halaman.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-lg-7">
                                    <div class="scan-panel">
                                        <div v-if="isLoading && !timeout" class="scan-state scan-state--loading">
                                            <div class="scan-loader">
                                                <div class="scan-loader__ring"></div>
                                                <i class="fab fa-whatsapp scan-loader__icon"></i>
                                            </div>
                                            <p class="scan-state__title">Menyiapkan koneksi...</p>
                                            <p class="scan-state__subtitle">Mohon tunggu sebentar</p>
                                        </div>

                                        <div v-else-if="passkeyLoading" class="scan-state">
                                            <div class="scan-state__icon scan-state__icon--info">
                                                <i class="fas fa-fingerprint"></i>
                                            </div>
                                            <p class="scan-state__title">Meminta verifikasi passkey...</p>
                                            <p class="scan-state__subtitle" v-if="passkeyNeedsExtension">
                                                Ikuti prompt passkey di tab WhatsApp Web
                                            </p>
                                            <p class="scan-state__subtitle" v-else>Ikuti prompt browser Anda</p>
                                            <div class="spinner-border text-primary mt-3" role="status"></div>
                                        </div>

                                        <div v-else-if="passkeyReady && passkeyNeedsExtension && !passkeyResolvable" class="scan-state scan-state--extension">
                                            <div class="scan-state__icon scan-state__icon--warning">
                                                <i class="fab fa-chrome"></i>
                                            </div>
                                            <p class="scan-state__title">Extension Chrome Diperlukan</p>
                                            <p class="scan-state__subtitle">
                                                WhatsApp meminta verifikasi passkey. Browser hanya mengizinkannya dari
                                                <strong>web.whatsapp.com</strong>, bukan dari dashboard WHACENTER.
                                                Pasang extension resmi kami agar verifikasi bisa diselesaikan dari sini.
                                            </p>

                                            <div class="extension-install-actions">
                                                <a
                                                    :href="passkeyExtensionDownloadUrl"
                                                    download="whacenter-passkey-extension.zip"
                                                    class="btn btn-primary btn-lg"
                                                >
                                                    <i class="fas fa-download mr-1"></i>
                                                    Download Extension
                                                </a>
                                                <button
                                                    type="button"
                                                    class="btn btn-outline-primary btn-lg"
                                                    v-on:click="recheckExtension"
                                                >
                                                    <i class="fas fa-sync mr-1"></i>
                                                    Cek Extension
                                                </button>
                                            </div>

                                            <div class="extension-guide">
                                                <p class="extension-guide__title">Cara memasang (sekali saja):</p>
                                                <ol class="extension-steps">
                                                    <li>Klik <strong>Download Extension</strong>, lalu extract file ZIP.</li>
                                                    <li>Buka Chrome, ketik <code>chrome://extensions</code> di address bar.</li>
                                                    <li>Aktifkan <strong>Developer mode</strong> (pojok kanan atas).</li>
                                                    <li>Klik <strong>Load unpacked</strong>, pilih folder <code>passkey-extension</code> hasil extract.</li>
                                                    <li>Kembali ke halaman ini, klik <strong>Cek Extension</strong>, lalu lanjutkan verifikasi passkey.</li>
                                                </ol>
                                            </div>

                                            <p class="scan-hint">
                                                <i class="fas fa-info-circle mr-1"></i>
                                                Gunakan Google Chrome. Setelah terpasang, extension akan membuka tab WhatsApp Web saat verifikasi passkey diminta.
                                            </p>
                                        </div>

                                        <div v-else-if="passkeyReady && !passkeyOptionsReady" class="scan-state">
                                            <div class="scan-state__icon scan-state__icon--info">
                                                <i class="fas fa-fingerprint"></i>
                                            </div>
                                            <p class="scan-state__title">Menyiapkan Verifikasi Passkey</p>
                                            <p class="scan-state__subtitle">Menunggu data passkey dari server WhatsApp...</p>
                                            <div class="spinner-border text-primary mt-3" role="status"></div>
                                        </div>

                                        <div v-else-if="passkeyReady" class="scan-state">
                                            <div class="scan-state__icon scan-state__icon--info">
                                                <i class="fas fa-fingerprint"></i>
                                            </div>
                                            <p class="scan-state__title">Verifikasi Passkey Diperlukan</p>
                                            <p class="scan-state__subtitle" v-if="passkeyNeedsExtension">
                                                Passkey Anda di HP? Klik Verifikasi Passkey → di tab WA Web klik tombol hijau →
                                                pilih <strong>Gunakan perangkat lain</strong> / scan QR → verifikasi di HP.
                                                Pastikan Bluetooth laptop aktif.
                                            </p>
                                            <p class="scan-state__subtitle" v-else>
                                                WhatsApp meminta verifikasi passkey. Ikuti prompt browser Anda.
                                            </p>
                                            <button class="btn btn-primary btn-lg mt-3" v-on:click="triggerPasskey" :disabled="passkeyLoading">
                                                <i class="fas fa-fingerprint mr-1"></i> Verifikasi Passkey
                                            </button>
                                        </div>

                                        <div v-else-if="passkeyConfirmReady" class="scan-state">
                                            <div class="scan-state__icon scan-state__icon--primary">
                                                <i class="fas fa-shield-alt"></i>
                                            </div>
                                            <p class="scan-state__title">Konfirmasi Passkey</p>
                                            <p class="scan-state__subtitle">Pastikan kode di HP Anda sama dengan kode berikut:</p>
                                            <div class="pairing-code">{{ passkeyCode }}</div>
                                            <button class="btn btn-primary btn-lg mt-3" v-on:click="confirmPasskey" :disabled="passkeyConfirmLoading">
                                                <span v-if="passkeyConfirmLoading" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                Konfirmasi Passkey
                                            </button>
                                        </div>

                                        <div v-else-if="phonePairingEnabled && pairingCodeReady" class="scan-state">
                                            <div class="scan-state__icon scan-state__icon--primary">
                                                <i class="fas fa-keyboard"></i>
                                            </div>
                                            <p class="scan-state__title">Masukkan Kode di WhatsApp</p>
                                            <p class="scan-state__subtitle">Ketik kode berikut di HP Anda</p>
                                            <div class="pairing-code pairing-code--large">{{ pairingCode }}</div>
                                            <div class="pairing-meta">
                                                <span v-if="deviceNumber"><i class="fas fa-phone mr-1"></i>{{ deviceNumber }}</span>
                                                <span><i class="fas fa-clock mr-1"></i>Berlaku ~2 menit</span>
                                            </div>
                                        </div>

                                        <div v-else-if="qrReady" class="scan-state scan-state--qr">
                                            <p class="scan-state__title">Scan QR Code</p>
                                            <p class="scan-state__subtitle">Arahkan kamera WhatsApp ke QR Code di bawah</p>
                                            <div class="qr-actions">
                                                <div class="qr-wrapper">
                                                    <qrcode :value="qr" :options="{ width: 280, margin: 2 }"></qrcode>
                                                </div>
                                                <button
                                                    type="button"
                                                    class="btn btn-outline-primary"
                                                    v-on:click="relogDevice()"
                                                    :disabled="pairingLoading"
                                                >
                                                    <span v-if="pairingLoading" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                    <i v-else class="fas fa-redo mr-1"></i>
                                                    Minta QR Baru
                                                </button>
                                            </div>
                                        </div>

                                        <div v-else-if="timeout" class="scan-state">
                                            <div class="scan-state__icon scan-state__icon--danger">
                                                <i class="fas fa-exclamation-triangle"></i>
                                            </div>
                                            <p class="scan-state__title">{{ timeoutMessage }}</p>
                                            <p class="scan-state__subtitle">Koneksi gagal atau waktu habis. Coba lagi.</p>
                                            <button class="btn btn-primary btn-lg mt-3" v-on:click="relogDevice()" :disabled="pairingLoading">
                                                <span v-if="pairingLoading" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                <i v-else class="fas fa-redo mr-1"></i>
                                                Minta QR Baru
                                            </button>
                                        </div>

                                        <div v-else class="scan-state">
                                            <div class="scan-state__icon scan-state__icon--warning">
                                                <i class="fas fa-plug"></i>
                                            </div>
                                            <p class="scan-state__title">Sesi Koneksi Terputus</p>
                                            <p class="scan-state__subtitle">
                                                Server WhatsApp baru di-restart atau sesi kedaluwarsa.
                                                Mulai ulang untuk mendapatkan QR Code baru.
                                            </p>
                                            <button class="btn btn-primary btn-lg mt-3" v-on:click="relogDevice()" :disabled="pairingLoading">
                                                <span v-if="pairingLoading" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                <i v-else class="fas fa-redo mr-1"></i>
                                                Mulai Ulang Koneksi
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <modal name="edit_number" :height="'auto'" :adaptive="true" :scrollable="true">
            <div class="edit-number-modal">
                <h5 class="mb-3">Edit Nomor HP</h5>
                <label>Nomor HP</label>
                <input type="number" class="form-control" v-model="editNumber" placeholder="628xxxxxxxxxx" />
                <p class="edit-number-hint">Gunakan format angka tanpa spasi, contoh: 628123456789</p>
                <button
                    type="button"
                    class="btn btn-primary btn-block mt-3"
                    v-on:click="saveNumber"
                    :disabled="numberSaving || !editNumber"
                >
                    <span v-if="numberSaving" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                    Simpan
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
    import {
        getPasskeyCredential,
        isPasskeySupported,
        isPasskeyResolvable,
        needsPasskeyExtension,
        checkPasskeyExtension,
        normalizePasskeyOptions,
        isPasskeyOptionsReady,
    } from '../../../utils/webauthn'

    const PHONE_PAIRING_ENABLED = false

    export default {
        data() {
            return {
                phonePairingEnabled: PHONE_PAIRING_ENABLED,
                isLoading: true,
                qrReady: false,
                timeout: false,
                timeoutMessage: 'Waktu Koneksi Habis',
                qr: "",
                device_id: "",
                deviceName: "",
                loop: true,
                reconnect: false,
                timeout_count: 0,
                interval: null,
                repairLoading: false,
                passkeyReady: false,
                passkeyLoading: false,
                passkeyConfirmReady: false,
                passkeyConfirmLoading: false,
                passkeyCode: "",
                passkeyOptions: null,
                passkeyTriggered: false,
                passkeyResolvable: false,
                passkeyNeedsExtension: false,
                pairingMode: "qr",
                pairingCodeReady: false,
                pairingCode: "",
                deviceNumber: "",
                pairingLoading: false,
                editNumber: "",
                numberSaving: false,
                sessionRelogAttempted: false,
                fastPollTimer: null,
                phoneCooldownUntil: 0,
                phoneCooldownTimer: null,
            }
        },
        computed: {
            statusLabel() {
                if (this.isLoading && !this.timeout) return 'Menyiapkan'
                if (this.passkeyLoading || this.passkeyReady || this.passkeyConfirmReady) return 'Verifikasi Passkey'
                if (this.pairingCodeReady) return 'Menunggu Kode'
                if (this.qrReady) return 'Menunggu Scan'
                if (this.timeout) return 'Gagal'
                return 'Menunggu'
            },
            statusClass() {
                if (this.timeout) return 'status-dot--danger'
                if (this.qrReady || this.pairingCodeReady) return 'status-dot--active'
                if (this.passkeyReady || this.passkeyConfirmReady) return 'status-dot--warning'
                return 'status-dot--pending'
            },
            phoneCooldownActive() {
                return this.phoneCooldownUntil > Date.now()
            },
            phoneCooldownSeconds() {
                if (!this.phoneCooldownActive) return 0
                return Math.ceil((this.phoneCooldownUntil - Date.now()) / 1000)
            },
            passkeyExtensionDownloadUrl() {
                const base = process.env.VUE_APP_FRONTEND_URL || ''
                if (base) {
                    return `${base.replace(/\/$/, '')}/cdn/whacenter-passkey-extension.zip`
                }
                return '/cdn/whacenter-passkey-extension.zip'
            },
            passkeyOptionsReady() {
                return isPasskeyOptionsReady(this.passkeyOptions)
            },
        },
        beforeDestroy() {
            this.$root.$off("ws", this.fn);
            clearInterval(this.interval)
            clearTimeout(this.fastPollTimer)
            clearInterval(this.phoneCooldownTimer)
        },
        async mounted() {
            this.device_id = this.$route.query.id
            const routeMode = this.$route.query.mode
            if (this.phonePairingEnabled && (routeMode === 'phone' || routeMode === 'qr')) {
                this.pairingMode = routeMode
            } else {
                this.pairingMode = 'qr'
            }
            this.passkeyNeedsExtension = needsPasskeyExtension()
            this.passkeyResolvable = await isPasskeyResolvable()
            this.loadData()
            this.interval = setInterval(this.loadData, 4000)
        },
        methods: {
            startPhoneCooldown(seconds = 90) {
                this.phoneCooldownUntil = Date.now() + (seconds * 1000)
                clearInterval(this.phoneCooldownTimer)
                this.phoneCooldownTimer = setInterval(() => {
                    if (!this.phoneCooldownActive) {
                        clearInterval(this.phoneCooldownTimer)
                        this.phoneCooldownTimer = null
                    }
                }, 1000)
            },
            pairingModeStorageKey() {
                return `whacenter_scan_mode_${this.device_id}`
            },
            loadPairingMode() {
                const mode = localStorage.getItem(this.pairingModeStorageKey())
                return mode === 'phone' || mode === 'qr' ? mode : null
            },
            savePairingMode(mode) {
                if (!this.device_id || (mode !== 'phone' && mode !== 'qr')) {
                    return
                }
                localStorage.setItem(this.pairingModeStorageKey(), mode)
            },
            resetPairingUI() {
                this.qrReady = false
                this.passkeyReady = false
                this.passkeyConfirmReady = false
                this.passkeyLoading = false
                this.passkeyConfirmLoading = false
                this.pairingCodeReady = false
                this.qr = ""
            },
            hasValidQr(qr) {
                return !!(qr && qr !== "passkey" && qr !== "passkey_confirm" && qr !== "pairing_code")
            },
            isOrphanedSession(device, passkey, pairing) {
                const qr = device.qr
                if (!qr || qr === "timeout" || qr === "dc") {
                    return false
                }
                if (qr === "pairing_code") {
                    return !(pairing && pairing.event === "pairing_code")
                }
                if (qr === "passkey") {
                    return !(passkey && passkey.event === "passkey_request")
                }
                if (qr === "passkey_confirm") {
                    return !(passkey && passkey.event === "passkey_confirmation")
                }
                return false
            },
            async recoverStaleSession() {
                if (this.sessionRelogAttempted || this.pairingLoading) {
                    return false
                }
                this.sessionRelogAttempted = true
                await this.relogDevice({ manual: false })
                return true
            },
            startFastPolling() {
                clearTimeout(this.fastPollTimer)
                this.pollIntervalMs = 1500
                clearInterval(this.interval)
                this.interval = setInterval(this.loadData, this.pollIntervalMs)
                this.fastPollTimer = setTimeout(() => {
                    this.pollIntervalMs = 4000
                    clearInterval(this.interval)
                    this.interval = setInterval(this.loadData, this.pollIntervalMs)
                }, 30000)
            },
            prepareModeSwitch(mode) {
                this.pairingMode = mode
                this.savePairingMode(mode)
                this.resetPairingUI()
                this.isLoading = true
                this.timeout = false
                this.qrReady = false
                this.pairingCodeReady = false
                this.sessionRelogAttempted = false
            },
            async switchPairingMode(mode) {
                if (!this.phonePairingEnabled && mode === 'phone') {
                    return
                }
                if (this.pairingMode === mode || this.pairingLoading) {
                    return
                }

                if (mode === 'phone') {
                    if (!this.qrReady && !this.pairingCodeReady) {
                        this.$toast.error('Tunggu QR muncul dulu')
                        return
                    }
                    if (!this.deviceNumber) {
                        this.$toast.error('Isi nomor HP terlebih dahulu')
                        this.openEditNumber()
                        return
                    }
                    this.prepareModeSwitch(mode)
                    this.startFastPolling()
                    await this.requestPairingCode()
                    return
                }

                this.prepareModeSwitch(mode)
                this.pairingLoading = false
                this.isLoading = false
                this.pairingCodeReady = false
                this.pairingCode = ""
                if (this.hasValidQr(this.qr)) {
                    this.qrReady = true
                } else {
                    this.loadData()
                }
            },
            openEditNumber() {
                this.editNumber = this.deviceNumber
                this.$modal.show('edit_number')
            },
            async saveNumber() {
                if (!this.editNumber) {
                    return
                }
                this.numberSaving = true
                try {
                    await this.$store.dispatch('device_update', {
                        id: parseInt(this.device_id),
                        name: this.deviceName || 'Device',
                        number: String(this.editNumber),
                    })
                    this.deviceNumber = String(this.editNumber)
                    this.$modal.hide('edit_number')
                    this.$toast.success('Nomor HP berhasil diperbarui')
                    if (this.pairingMode === 'phone' && (this.qrReady || this.pairingCodeReady)) {
                        await this.requestPairingCode()
                    }
                } catch (err) {
                    this.$toast.error(err.response?.data?.message || 'Gagal memperbarui nomor')
                } finally {
                    this.numberSaving = false
                }
            },
            async loadData() {
                try {
                    let detail = await this.$store.dispatch("device_detail", {
                        id: this.device_id
                    })

                    const device = detail.data.data.device
                    const passkey = detail.data.data.passkey
                    const pairing = detail.data.data.pairing
                    this.qr = device.qr
                    this.deviceNumber = device.number
                    this.deviceName = device.name

                    if (device.status == "CONNECTED") {
                        localStorage.removeItem(this.pairingModeStorageKey())
                        this.$toast.info("Device sudah terkoneksi")
                        this.$router.push("/device")
                        return
                    }

                    if (this.pairingLoading && this.pairingMode === 'phone') {
                        this.qrReady = false
                        if (!pairing || (pairing.event !== 'pairing_code' && pairing.event !== 'pairing_error')) {
                            this.isLoading = true
                            return
                        }
                    }

                    if (this.pairingLoading && this.pairingMode === 'qr') {
                        this.pairingCodeReady = false
                        if (!this.hasValidQr(device.qr)) {
                            this.qrReady = false
                            this.isLoading = true
                            return
                        }
                    }

                    if (pairing && pairing.event === "pairing_code") {
                        if (!this.phonePairingEnabled) {
                            if (this.hasValidQr(device.qr)) {
                                this.pairingMode = 'qr'
                                this.pairingCodeReady = false
                                this.qrReady = true
                                this.isLoading = false
                                return
                            }
                            if (!this.sessionRelogAttempted) {
                                await this.relogDevice({ manual: false, quiet: true })
                            }
                            return
                        }
                        this.pairingMode = "phone"
                        this.savePairingMode("phone")
                        this.timeout = false
                        this.isLoading = false
                        this.qrReady = false
                        this.passkeyReady = false
                        this.pairingCode = pairing.code
                        this.pairingCodeReady = true
                        return
                    }

                    if (pairing && pairing.event === "pairing_error") {
                        this.pairingMode = "qr"
                        this.savePairingMode("qr")
                        this.pairingLoading = false
                        this.isLoading = false
                        this.pairingCodeReady = false
                        this.timeout = false
                        const errMsg = pairing.error || "Pairing gagal"
                        this.$toast.error(errMsg)
                        if (/percobaan|rate|429|detik/i.test(errMsg)) {
                            const match = errMsg.match(/(\d+)\s*detik/)
                            this.startPhoneCooldown(match ? parseInt(match[1], 10) : 90)
                        }
                        if (this.hasValidQr(device.qr)) {
                            this.qr = device.qr
                            this.qrReady = true
                        }
                        return
                    }

                    if (passkey && passkey.event === "passkey_request") {
                        this.timeout = false
                        this.isLoading = false
                        this.qrReady = false
                        this.passkeyConfirmReady = false
                        this.passkeyOptions = normalizePasskeyOptions(passkey.passkey_options)
                        this.passkeyReady = true
                        this.passkeyTriggered = false
                        this.passkeyNeedsExtension = needsPasskeyExtension()
                        this.passkeyResolvable = await isPasskeyResolvable()
                        return
                    }

                    if (passkey && passkey.event === "passkey_confirmation") {
                        this.timeout = false
                        this.isLoading = false
                        this.qrReady = false
                        this.passkeyReady = false
                        this.passkeyLoading = false
                        this.passkeyCode = passkey.code
                        this.passkeyConfirmReady = true
                        return
                    }

                    if (passkey && passkey.event === "passkey_error") {
                        this.isLoading = false
                        this.resetPairingUI()
                        this.passkeyTriggered = false
                        this.timeout = true
                        this.timeoutMessage = passkey.error || "Passkey gagal"
                        return
                    }

                    this.passkeyReady = false
                    this.passkeyConfirmReady = false
                    this.passkeyLoading = false
                    this.pairingCodeReady = false
                    this.timeout = false

                    if (device.qr == "timeout" || device.qr == "dc" || device.qr == null) {
                        this.qrReady = false
                        this.isLoading = false
                        this.timeout = true
                        this.timeoutMessage = "Waktu Koneksi Habis"
                        if (device.qr == "timeout" && this.timeout_count >= 3) {
                            this.loop = false
                        } else if (device.qr == "timeout") {
                            this.timeout_count += 1
                        }
                        return
                    }

                    if (this.isOrphanedSession(device, passkey, pairing)) {
                        this.qrReady = false
                        this.pairingCodeReady = false
                        if (await this.recoverStaleSession()) {
                            return
                        }
                        this.isLoading = false
                        return
                    }

                    if (this.pairingMode === "phone" && !this.pairingCodeReady && !this.pairingLoading) {
                        this.isLoading = true
                        return
                    }

                    if (this.hasValidQr(device.qr) && !this.pairingLoading) {
                        this.passkeyTriggered = false
                        this.qrReady = true
                        this.isLoading = false
                        if (this.pairingMode === 'qr') {
                            this.savePairingMode('qr')
                        }
                    } else if (!this.pairingCodeReady) {
                        this.qrReady = false
                        if (!this.sessionRelogAttempted && !this.pairingLoading) {
                            if (await this.recoverStaleSession()) {
                                return
                            }
                        }
                        this.isLoading = false
                    }
                } catch (err) {
                    this.$toast.error(err.response.data.message)
                    this.$router.push("/device")
                }
            },
            async refreshPasskeyOptions() {
                const detail = await this.$store.dispatch('device_detail', { id: this.device_id })
                const passkey = detail.data.data.passkey
                if (passkey && passkey.event === 'passkey_request' && passkey.passkey_options) {
                    this.passkeyOptions = normalizePasskeyOptions(passkey.passkey_options)
                }
                return this.passkeyOptions
            },
            async recheckExtension() {
                this.passkeyResolvable = await checkPasskeyExtension()
                if (this.passkeyResolvable) {
                    this.$toast.success("Extension terdeteksi")
                    await this.refreshPasskeyOptions()
                    if (this.passkeyReady && !this.passkeyTriggered && this.passkeyOptionsReady) {
                        this.triggerPasskey()
                    }
                } else {
                    this.$toast.error("Extension belum terpasang atau belum aktif")
                }
            },
            async triggerPasskey() {
                if (this.passkeyTriggered || this.passkeyLoading) {
                    return
                }

                this.passkeyNeedsExtension = needsPasskeyExtension()
                await this.refreshPasskeyOptions()
                this.passkeyOptions = normalizePasskeyOptions(this.passkeyOptions)
                if (!isPasskeyOptionsReady(this.passkeyOptions)) {
                    this.$toast.info('Data passkey belum siap, tunggu sebentar...')
                    this.passkeyReady = true
                    return
                }

                if (this.passkeyNeedsExtension) {
                    this.passkeyResolvable = await isPasskeyResolvable()
                    if (!this.passkeyResolvable) {
                        this.passkeyReady = true
                        this.passkeyTriggered = false
                        return
                    }
                } else if (!isPasskeySupported()) {
                    this.$toast.error("Browser tidak mendukung passkey")
                    return
                }

                this.passkeyTriggered = true
                this.passkeyLoading = true
                this.passkeyReady = false

                try {
                    const passkeyResponse = await getPasskeyCredential(this.passkeyOptions)
                    await this.$store.dispatch("device_passkey_response", {
                        id: parseInt(this.device_id),
                        passkey_response: passkeyResponse,
                    })
                    this.$toast.success("Passkey berhasil dikirim, menunggu konfirmasi...")
                } catch (err) {
                    this.passkeyTriggered = false
                    this.passkeyReady = true
                    const message = err.response?.data?.message || err.message || "Passkey gagal"
                    if (message === 'EXTENSION_REQUIRED') {
                        this.passkeyResolvable = false
                        return
                    }
                    this.$toast.error(message)
                } finally {
                    this.passkeyLoading = false
                }
            },
            async confirmPasskey() {
                this.passkeyConfirmLoading = true
                try {
                    const response = await this.$store.dispatch("device_passkey_confirm", {
                        id: parseInt(this.device_id),
                    })
                    this.$toast.success(response.data.message)
                    this.$toast.info("Menunggu koneksi device...")
                    this.passkeyConfirmReady = false
                } catch (err) {
                    this.$toast.error(err.response?.data?.message || "Konfirmasi passkey gagal")
                } finally {
                    this.passkeyConfirmLoading = false
                }
            },
            async requestPairingCode() {
                if (this.pairingLoading) {
                    return
                }
                if (!this.deviceNumber) {
                    this.$toast.error('Isi nomor HP terlebih dahulu')
                    this.openEditNumber()
                    return
                }
                this.pairingLoading = true
                try {
                    await this.$store.dispatch('device_request_pairing_code', {
                        id: parseInt(this.device_id),
                        number: String(this.deviceNumber),
                    })
                    this.isLoading = true
                    this.timeout = false
                    this.pairingCodeReady = false
                    this.pairingCode = ""
                    this.loadData()
                } catch (err) {
                    this.isLoading = false
                    const errMsg = err.response?.data?.message || 'Gagal meminta kode pairing'
                    this.$toast.error(errMsg)
                    if (/percobaan|rate|429|detik/i.test(errMsg)) {
                        const match = errMsg.match(/(\d+)\s*detik/)
                        this.startPhoneCooldown(match ? parseInt(match[1], 10) : 90)
                    }
                } finally {
                    this.pairingLoading = false
                }
            },
            async relogDevice({ manual = true, quiet = false } = {}) {
                if (this.pairingLoading) {
                    return
                }
                if (manual) {
                    this.sessionRelogAttempted = false
                }
                this.pairingMode = 'qr'
                this.savePairingMode('qr')
                this.pairingLoading = true
                try {
                    await this.$store.dispatch('device_relog', {
                        id: parseInt(this.device_id),
                        pair_method: 'qr',
                    })
                    this.sessionRelogAttempted = true
                    this.isLoading = true
                    this.timeout = false
                    this.passkeyTriggered = false
                    this.resetPairingUI()
                    if (!quiet) {
                        this.$toast.success('Memulai ulang koneksi...')
                    }
                    this.loadData()
                } catch (err) {
                    this.isLoading = false
                    this.$toast.error(err.response?.data?.message || 'Gagal memulai ulang koneksi')
                } finally {
                    this.pairingLoading = false
                }
            },
            async repairDevice() {
                this.repairLoading = true
                try {
                    let response = await this.$store.dispatch('device_move', {
                        id: parseInt(this.device_id),
                    })
                    this.$router.go(0)
                    this.$toast.success(response.data.message);
                } catch (err) {
                    this.$toast.error(err.response.data.message);
                } finally {
                    this.repairLoading = false
                }
            },
        }
    }
</script>

<style scoped>
.scan-card {
    overflow: hidden;
}

.scan-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px 30px;
    background: #f8f9fb;
    border-bottom: 1px solid #e3e6ef;
    color: #272b41;
}

.scan-header__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #e8f8ef;
    color: #25d366;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    flex-shrink: 0;
}

.scan-header__info {
    flex: 1;
    min-width: 0;
}

.scan-header__info h5 {
    color: #272b41;
    font-weight: 600;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.scan-header__number {
    font-size: 13px;
    color: #9299b8;
}

.scan-header__number-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-edit-number {
    border: none;
    background: #fff;
    border: 1px solid #e3e6ef;
    color: #5a5f7d;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
}

.btn-edit-number:hover {
    color: #5f63f2;
    border-color: #5f63f2;
}

.edit-number-modal {
    padding: 24px;
}

.edit-number-hint {
    font-size: 12px;
    color: #9299b8;
    margin-top: 8px;
    margin-bottom: 0;
}

.scan-header__status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #5a5f7d;
    background: #fff;
    border: 1px solid #e3e6ef;
    padding: 6px 14px;
    border-radius: 20px;
    white-space: nowrap;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #9299b8;
    animation: pulse 2s infinite;
}

.status-dot--active { background: #25d366; }
.status-dot--pending { background: #9299b8; animation: none; }
.status-dot--warning { background: #ffc107; }
.status-dot--danger { background: #ff4d4f; animation: none; }

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
}

.scan-body {
    padding: 30px;
}

.scan-section-title {
    font-weight: 600;
    color: #272b41;
    margin-bottom: 20px;
    font-size: 16px;
}

.scan-steps {
    padding-left: 0;
    list-style: none;
    counter-reset: step;
    margin-bottom: 28px;
}

.scan-steps li {
    counter-increment: step;
    position: relative;
    padding: 10px 0 10px 40px;
    font-size: 14px;
    color: #5a5f7d;
    line-height: 1.5;
    border-left: 2px solid #e3e6ef;
    margin-left: 14px;
}

.scan-steps li:last-child {
    border-left-color: transparent;
}

.scan-steps li::before {
    content: counter(step);
    position: absolute;
    left: -15px;
    top: 8px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f4f5f7;
    color: #5f63f2;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #e3e6ef;
}

.mode-toggle {
    display: flex;
    gap: 0;
    background: #f4f5f7;
    border-radius: 10px;
    padding: 4px;
    margin-bottom: 24px;
}

.mode-toggle__btn {
    flex: 1;
    border: none;
    background: transparent;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #5a5f7d;
    cursor: pointer;
    transition: all 0.2s ease;
}

.mode-toggle__btn.active {
    background: #fff;
    color: #5f63f2;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.mode-toggle__btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.scan-actions {
    margin-top: 8px;
}

.scan-hint {
    font-size: 12px;
    color: #9299b8;
    margin-top: 12px;
    margin-bottom: 0;
    line-height: 1.5;
}

.scan-panel {
    background: #f8f9fb;
    border-radius: 16px;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 30px;
    border: 1px solid #e3e6ef;
}

.scan-state {
    text-align: center;
    width: 100%;
}

.scan-state__icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    font-size: 28px;
}

.scan-state__icon--info {
    background: #e8f4fd;
    color: #2c99ff;
}

.scan-state__icon--primary {
    background: #eeeaff;
    color: #5f63f2;
}

.scan-state__icon--danger {
    background: #fff0f0;
    color: #ff4d4f;
}

.scan-state__icon--warning {
    background: #fff8e6;
    color: #fa8c16;
}

.scan-state--extension {
    max-width: 520px;
    margin: 0 auto;
    text-align: left;
}

.scan-state--extension .scan-state__icon,
.scan-state--extension .scan-state__title,
.scan-state--extension .scan-state__subtitle {
    text-align: center;
}

.extension-install-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin: 24px 0 20px;
}

.extension-install-actions .btn {
    min-width: 200px;
}

.extension-guide {
    background: #fff;
    border: 1px solid #e3e6ef;
    border-radius: 12px;
    padding: 16px 18px 12px;
    margin-bottom: 16px;
}

.extension-guide__title {
    font-size: 13px;
    font-weight: 600;
    color: #272b41;
    margin-bottom: 12px;
}

.extension-steps {
    list-style: none;
    counter-reset: ext-step;
    margin: 0;
    padding: 0;
}

.extension-steps li {
    position: relative;
    padding: 0 0 14px 36px;
    font-size: 13px;
    color: #5a5f7d;
    line-height: 1.55;
}

.extension-steps li:last-child {
    padding-bottom: 0;
}

.extension-steps li::before {
    content: counter(ext-step);
    counter-increment: ext-step;
    position: absolute;
    left: 0;
    top: 1px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #eeeaff;
    color: #5f63f2;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.extension-steps code {
    font-size: 12px;
    color: #5f63f2;
    background: #f4f5f7;
    padding: 1px 6px;
    border-radius: 4px;
}

.scan-state__title {
    font-size: 18px;
    font-weight: 600;
    color: #272b41;
    margin-bottom: 6px;
}

.scan-state__subtitle {
    font-size: 14px;
    color: #9299b8;
    margin-bottom: 0;
}

.scan-loader {
    position: relative;
    width: 80px;
    height: 80px;
    margin: 0 auto 20px;
}

.scan-loader__ring {
    width: 80px;
    height: 80px;
    border: 3px solid #e3e6ef;
    border-top-color: #25d366;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.scan-loader__icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 28px;
    color: #25d366;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.pairing-code {
    display: inline-block;
    font-size: 36px;
    font-weight: 700;
    letter-spacing: 8px;
    color: #272b41;
    background: #fff;
    border: 2px dashed #5f63f2;
    border-radius: 12px;
    padding: 16px 32px;
    margin: 20px 0 12px;
    font-family: 'SF Mono', 'Fira Code', monospace;
}

.pairing-code--large {
    font-size: 42px;
    letter-spacing: 10px;
}

.pairing-meta {
    display: flex;
    justify-content: center;
    gap: 20px;
    font-size: 13px;
    color: #9299b8;
}

.qr-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
}

.qr-wrapper {
    display: block;
    background: #fff;
    padding: 16px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

@media (max-width: 991px) {
    .scan-panel {
        margin-top: 24px;
        min-height: 320px;
    }

    .scan-header {
        flex-wrap: wrap;
    }

    .scan-header__status {
        width: 100%;
        justify-content: center;
        margin-top: 4px;
    }

    .pairing-code {
        font-size: 28px;
        letter-spacing: 6px;
        padding: 12px 20px;
    }

    .pairing-code--large {
        font-size: 32px;
        letter-spacing: 6px;
    }
}
</style>
